import React from 'react';
import { Check, Package, Layers, FlaskConical, Warehouse } from 'lucide-react';
import { cn } from '@/lib/utils';

const steps = [
  { id: 'recepcao', label: 'Recepção', icon: Package, description: 'Material recebido' },
  { id: 'montagem', label: 'Montagem', icon: Layers, description: 'Kit preparado' },
  { id: 'ciclo', label: 'Ciclo', icon: FlaskConical, description: 'Em esterilização' },
  { id: 'arsenal', label: 'Arsenal', icon: Warehouse, description: 'Pronto para entrega' },
];

const statusMap = {
  // Recepção statuses
  'pendente': 0,
  'aguardando_montagem': 0,
  'processado': 1,
  // Montagem statuses
  'em_montagem': 1,
  'pronto': 1,
  'em_ciclo': 2,
  // Ciclo statuses
  'em_processo': 2,
  'aprovado': 3,
  'reprovado': 2,
  // Arsenal
  'no_arsenal': 3,
  'entregue': 4,
};

export function ProgressSteps({ currentStatus = 'pendente', className }) {
  const currentStep = statusMap[currentStatus] ?? 0;
  
  return (
    <div className={cn("w-full", className)}>
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          const isLast = index === steps.length - 1;
          
          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300",
                    isCompleted && "bg-green-500 border-green-500 text-white",
                    isCurrent && "bg-blue-500 border-blue-500 text-white animate-pulse",
                    !isCompleted && !isCurrent && "bg-slate-100 border-slate-300 text-slate-400"
                  )}
                >
                  {isCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <StepIcon className="w-5 h-5" />
                  )}
                </div>
                <span
                  className={cn(
                    "mt-2 text-xs font-medium text-center",
                    isCompleted && "text-green-600",
                    isCurrent && "text-blue-600",
                    !isCompleted && !isCurrent && "text-slate-400"
                  )}
                >
                  {step.label}
                </span>
                <span
                  className={cn(
                    "text-[10px] text-center max-w-[80px]",
                    isCompleted && "text-green-500",
                    isCurrent && "text-blue-500",
                    !isCompleted && !isCurrent && "text-slate-300"
                  )}
                >
                  {step.description}
                </span>
              </div>
              
              {!isLast && (
                <div
                  className={cn(
                    "flex-1 h-1 mx-2 rounded-full transition-all duration-300",
                    index < currentStep && "bg-green-500",
                    index === currentStep && "bg-gradient-to-r from-blue-500 to-slate-200",
                    index > currentStep && "bg-slate-200"
                  )}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// Compact version for tables/cards
export function ProgressStepsCompact({ currentStatus = 'pendente', className }) {
  const currentStep = statusMap[currentStatus] ?? 0;
  
  return (
    <div className={cn("flex items-center gap-1", className)}>
      {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isCurrent = index === currentStep;
        
        return (
          <div
            key={step.id}
            title={`${step.label}: ${step.description}`}
            className={cn(
              "w-2 h-2 rounded-full transition-all",
              isCompleted && "bg-green-500",
              isCurrent && "bg-blue-500 animate-pulse",
              !isCompleted && !isCurrent && "bg-slate-300"
            )}
          />
        );
      })}
      <span className="ml-2 text-xs text-slate-500">
        {steps[Math.min(currentStep, steps.length - 1)]?.label}
      </span>
    </div>
  );
}
